// src/pages/RosterViewPage.js
import React, { useState, useEffect } from "react";
import "./RosterViewPage.css";
import apiClient from "../utils/apiClient"; // ✅ global API client
import SessionHandler from "../components/SessionHandler"; // ✅ global session expiry modal

const RosterViewPage = () => {
  const [departments, setDepartments] = useState([]);
  const [selectedDept, setSelectedDept] = useState("");
  const [selectedMonth, setSelectedMonth] = useState(
    new Date().toISOString().substr(0, 7)
  );
  const [rosterData, setRosterData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // ✅ Fetch departments (Admin hidden)
  useEffect(() => {
    const fetchDepartments = async () => {
      try {
        const data = await apiClient("/auth/allemployees/");
        if (!data) return; // handled by apiClient on 401

        const hiddenDepartments = ["admin"];

        const uniqueDepartments = [
          ...new Set(
            data
              .map((emp) => emp.department)
              .filter(
                (dept) =>
                  dept &&
                  !hiddenDepartments.includes(dept.toLowerCase())
              )
          ),
        ];

        setDepartments(uniqueDepartments);
      } catch (err) {
        console.error("Error fetching departments:", err);
        setError(err.message || "Failed to fetch departments");
      }
    };

    fetchDepartments();
  }, []);

  // ✅ Fetch roster data
  useEffect(() => {
    if (!selectedDept || !selectedMonth) return;

    const fetchRoster = async () => {
      setLoading(true);
      try {
        const url = `/roster/view-roster/?department=${selectedDept}&month=${selectedMonth}`;
        const data = await apiClient(url);
        if (!data) return; // handled globally

        setRosterData(data);
      } catch (err) {
        console.error("Error fetching roster:", err);
        setError(err.message || "Failed to fetch roster");
      } finally {
        setLoading(false);
      }
    };

    fetchRoster();
  }, [selectedDept, selectedMonth]);

  return (
    <div className="roster-container">
      <h2>Roster - Weekly View (Mon to Sun)</h2>

      <div className="roster-controls">
        <div>
          <label>Department:</label>
          <select
            value={selectedDept}
            onChange={(e) => setSelectedDept(e.target.value)}
          >
            <option value="">-- Select Department --</option>
            {departments.map((dept, idx) => (
              <option key={idx} value={dept}>
                {dept}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label>Month:</label>
          <input
            type="month"
            value={selectedMonth}
            onChange={(e) => setSelectedMonth(e.target.value)}
          />
        </div>
      </div>

      {loading && <div>Loading roster...</div>}
      {error && <div className="roster-error">{error}</div>}

      {!loading && !error && rosterData.length > 0 && (
        <div className="roster-table-wrapper">
          <table className="roster-table">
            <thead>
              <tr>
                <th>Employee Name</th>
                {["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"].map((day) => (
                  <th key={day}>{day}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rosterData.map((emp) => (
                <tr key={emp.id}>
                  <td>{emp.name}</td>
                  {["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"].map((day) => {
                    const entry = emp.roster?.[day];
                    return (
                      <td key={day} className={entry?.wfo ? "wfo-cell" : ""}>
                        <span className="time">{entry?.time || ""}</span>
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {!loading && !error && rosterData.length === 0 && selectedDept && (
        <div>No roster data found.</div>
      )}

      {/* ✅ Global session expiry modal */}
      <SessionHandler />
    </div>
  );
};

export default RosterViewPage;
